import { useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CRAWLER_STATUS } from '../../../../constants/crawlerConstants';
import { buildPath, PRIVATE_ROUTE } from '../../../../routes/routes';
import { sentenceCase } from '../../../../utils/utils';
import Dropdown from '../../../elements/dropdown/Dropdown';
import Text from '../../../elements/text/Text';
import Modal from '../../../sections/modal/Modal';
import ModalButtons from '../../../sections/modal/ModalButtons';

const ActionButtons = ({
  job,
  isAdmin,
  onRunJob,
  onStopJob,
  onResetJob,
  onEditJob,
  onDeleteJob,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [resetModal, setResetModal] = useState(false);

  // Dropdown menu
  const handleMenuClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Delete modal
  const openDeleteModal = () => {
    handleMenuClose();
    setDeleteModal(true);
  };
  const closeDeleteModal = () => setDeleteModal(false);

  // Reset modal
  const openResetModal = () => {
    handleMenuClose();
    setResetModal(true);
  };
  const closeResetModal = () => setResetModal(false);

  // Actions
  const handleStop = () => onStopJob(job.id);
  const handleRun = () => onRunJob(job.id);
  const handleReset = () => {
    onResetJob(job.id);
    closeResetModal();
  };
  const handleEdit = () => {
    handleMenuClose();
    onEditJob(job.id);
  };
  const handleDelete = async () => {
    handleMenuClose();
    await onDeleteJob(job.id);
    closeDeleteModal();
  };

  const isJobAvailable = (status) =>
    status === CRAWLER_STATUS.RUNNING || status === CRAWLER_STATUS.WAITING;
  const isJobRunning = (status) => status === CRAWLER_STATUS.RUNNING;

  const dropdownButtons = [
    {
      ariaLabel: 'view job details',
      actions: () =>
        navigate(
          buildPath({
            path: PRIVATE_ROUTE.PROJECT_CRAWLER_DETAILS,
            params: {
              projectId: job.projectId,
              crawlerJobId: job.id,
            },
          })
        ),
      disabled: false,
      label: t('table.viewJobDetails', { ns: 'crawler' }),
    },
    {
      ariaLabel: 'view job status',
      actions: () =>
        navigate(
          buildPath({
            path: PRIVATE_ROUTE.PROJECT_CRAWLER_JOB_STATUS,
            params: {
              projectId: job.projectId,
              crawlerJobId: job.id,
            },
          })
        ),
      disabled: false,
      label: t('table.viewJobStatus', { ns: 'crawler' }),
    },
    {
      ariaLabel: 'edit job',
      actions: handleEdit,
      disabled: job.status !== CRAWLER_STATUS.WAITING,
      label: t('table.editJob', { ns: 'crawler' }),
    },
    {
      ariaLabel: 'reset job',
      actions: openResetModal,
      disabled: job.status !== CRAWLER_STATUS.WAITING,
      label: t('table.resetJob', { ns: 'crawler' }),
    },
  ];

  return (
    <>
      {isJobAvailable(job.status) && (
        <>
          {isJobRunning(job.status) ? (
            <IconButton onClick={handleStop} size="large">
              <Tooltip title={t('table.stopJob', { ns: 'crawler' })}>
                <StopIcon aria-label="stop job" />
              </Tooltip>
            </IconButton>
          ) : (
            <IconButton onClick={handleRun} size="large">
              <Tooltip title={t('table.runJob', { ns: 'crawler' })}>
                <PlayArrowIcon aria-label="run job" />
              </Tooltip>
            </IconButton>
          )}
        </>
      )}
      <IconButton onClick={handleMenuClick} size="large">
        <MoreVertIcon aria-label="more actions" />
      </IconButton>

      <Dropdown anchorEl={anchorEl} onClose={handleMenuClose}>
        {dropdownButtons.map((button) => (
          <MenuItem
            key={button.ariaLabel}
            aria-label={button.ariaLabel}
            onClick={button.actions}
            disabled={button.disabled}
          >
            {button.label}
          </MenuItem>
        ))}
        {isAdmin && (
          <MenuItem
            aria-label="delete job"
            onClick={openDeleteModal}
            disabled={job.status !== CRAWLER_STATUS.WAITING}
          >
            {t('table.deleteJob', { ns: 'crawler' })}
          </MenuItem>
        )}
      </Dropdown>

      <Modal
        openModal={deleteModal}
        title={t('crawler.deleteJob', { ns: 'sectionTitles' })}
        handleClose={closeDeleteModal}
        action={
          <ModalButtons
            cancelProps={{ type: 'reset' }}
            actionProps={{ 'data-testid': 'modal-confirm' }}
            danger
            actionText={t('delete', { ns: 'buttons' })}
            onCancel={closeDeleteModal}
            onAction={handleDelete}
          />
        }
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('cantUndo', { ns: 'descriptions' })}
        </Alert>
        <Text body style={{ marginBottom: '1em' }}>
          <Trans
            i18nKey="paidCrawlerWarning"
            ns="descriptions"
            components={[<b key="0" />]}
          />
        </Text>
        <Text bodyBold style={{ marginBottom: '1em' }}>
          {t('editCrawlJobInstead', { ns: 'descriptions' })}
        </Text>

        <Text body style={{ marginBottom: '1em' }}>
          {sentenceCase(
            t('areYouSure', {
              ns: 'descriptions',
              text: job.name,
            })
          )}
        </Text>
      </Modal>

      <Modal
        openModal={resetModal}
        title={t('crawler.resetJob', { ns: 'sectionTitles' })}
        showActions
        handleClose={closeResetModal}
        handleConfirm={handleReset}
      >
        <Text body>{t('crawlerResetWarning', { ns: 'descriptions' })}</Text>
      </Modal>
    </>
  );
};

ActionButtons.propTypes = {
  job: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  onRunJob: PropTypes.func.isRequired,
  onStopJob: PropTypes.func.isRequired,
  onResetJob: PropTypes.func.isRequired,
  onEditJob: PropTypes.func.isRequired,
  onDeleteJob: PropTypes.func.isRequired,
};

export default ActionButtons;
